import {
  ComponentType,
  memo,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {CSSProperties} from 'react';
import styled from 'styled-components';
import {useLocation, useSearchParams} from 'react-router-dom';
import {NotificationCard} from '@ui/molecules/notification/notification';
import {NavigationOption} from '@ui/interface';
import {getColorFromTheme} from '@ui/ui-utils';

const OuterWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  background: ${getColorFromTheme('backgroundLight')};
`;

export const AlertsIndicator = styled.div`
  display: flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 5px;
  border-radius: 20px;
  background: ${getColorFromTheme('red400')};
  color: ${getColorFromTheme('alwaysWhite')};
  font-size: 11px;
  font-weight: 500;
`;

const Wrapper = styled.main<{$rail: boolean}>`
  position: relative;
  min-width: 0;
  flex: 1;
  height: 100vh;
  overflow-y: auto;

  @media (max-width: 767px) {
    width: 100%;
    padding-top: ${({$rail}) => ($rail ? '56px' : '0')};
    box-sizing: border-box;
  }
`;

export interface DashboardTemplateProps extends PropsWithChildren<{}> {
  header: ReactNode;
  headerPlacement?: 'rail' | 'top';
  dashboardStyle?: CSSProperties;
  outerStyle?: CSSProperties;
  navigations: NavigationOption[];
  workspace?: {avatar?: string; name: string};
  WorkspaceDropdown?: ComponentType<{onClose: () => void}>;
}

export const DashboardTemplate = memo(
  ({
    children,
    header,
    headerPlacement = 'rail',
    dashboardStyle,
    outerStyle,
  }: DashboardTemplateProps) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const showGroupDrawer = searchParams.get('group-drawer');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const usesFeatureShell = [
      '/goals',
      '/checkins',
      '/feedback',
      '/reporting',
      '/edit',
      '/workspace-settings',
    ].some((route) => location.pathname.startsWith(route));

    useEffect(() => {
      const checkModalVisibility = () => {
        const modalCard = document.getElementById('modal-card');
        const modal = document.getElementById('modal');
        setIsModalVisible(!!modalCard && !!modal);
      };
      checkModalVisibility();
      const observer = new MutationObserver(checkModalVisibility);
      observer.observe(document.body, {childList: true, subtree: true});
      return () => observer.disconnect();
    }, []);

    return (
      <OuterWrapper style={outerStyle}>
        <div
          className={`flex h-screen w-full overflow-hidden ${
            headerPlacement === 'top' ? 'flex-col' : 'flex-row'
          }`}
          data-testid="dashboard-frame"
          data-header-placement={headerPlacement}>
          {header}
          <Wrapper id="dashboard-body" $rail={headerPlacement === 'rail'}>
            <div className="fixed top-0 z-[4] w-full">
              {!showGroupDrawer && !isModalVisible && <NotificationCard />}
            </div>
            <div
              data-testid="dashboard-content"
              data-feature-shell={usesFeatureShell ? 'true' : 'false'}
              className={`min-h-screen overflow-x-hidden ${
                usesFeatureShell ? '' : 'px-10 pb-10 pt-4 sm:px-4'
              }`}
              style={dashboardStyle}>
              {children}
            </div>
          </Wrapper>
        </div>
      </OuterWrapper>
    );
  },
);

DashboardTemplate.displayName = 'DashboardTemplate';
